import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import AdminService, { AuditLogEntry } from '../../services/admin/admin.service';

export const AuditLogPage: React.FC = () => {
  const { accessToken } = useAuth();
  const [actions, setActions] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);

  useEffect(() => {
    fetchAuditLog();
  }, [page]);

  const fetchAuditLog = async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await AdminService.getAuditLog(accessToken, page, 50);
      setActions(data.actions);
      setPagination(data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load audit log');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'BAN':
        return '#dc2626';
      case 'UNBAN':
        return '#16a34a';
      case 'WARN':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'BAN':
        return '🚫';
      case 'UNBAN':
        return '✅';
      case 'WARN':
        return '⚠️';
      default:
        return '📝';
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Audit Log</h1>
        <p style={styles.loading}>Loading audit log...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Audit Log</h1>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Audit Log</h1>
        <button onClick={fetchAuditLog} style={styles.refreshButton}>
          🔄 Refresh
        </button>
      </div>

      <p style={styles.description}>
        Track all administrative actions performed on the platform.
      </p>

      {/* Audit Log List */}
      <div style={styles.logList}>
        {actions.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No audit log entries found</p>
          </div>
        ) : (
          actions.map((action) => (
            <div key={action.id} style={styles.logEntry}>
              <div
                style={{
                  ...styles.actionIcon,
                  color: getActionColor(action.action),
                }}
              >
                {getActionIcon(action.action)}
              </div>

              <div style={styles.logContent}>
                <div style={styles.logHeader}>
                  <span
                    style={{
                      ...styles.actionBadge,
                      backgroundColor: getActionColor(action.action),
                    }}
                  >
                    {action.action}
                  </span>
                  <span style={styles.logDate}>{formatDate(action.createdAt)}</span>
                </div>

                <div style={styles.logDetails}>
                  <div style={styles.logRow}>
                    <span style={styles.logLabel}>Admin:</span>
                    <span style={styles.logValue}>
                      {action.admin ? action.admin.email : 'System (Automated)'}
                    </span>
                  </div>

                  <div style={styles.logRow}>
                    <span style={styles.logLabel}>Target User:</span>
                    <span style={styles.logValue}>{action.targetUser.email}</span>
                  </div>

                  {action.reason && (
                    <div style={styles.logRow}>
                      <span style={styles.logLabel}>Reason:</span>
                      <span style={styles.logValue}>{action.reason}</span>
                    </div>
                  )}

                  {action.metadata && Object.keys(action.metadata).length > 0 && (
                    <div style={styles.logRow}>
                      <span style={styles.logLabel}>Metadata:</span>
                      <span style={styles.logValue}>
                        {JSON.stringify(action.metadata, null, 2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              ...styles.paginationButton,
              ...(page === 1 ? styles.paginationButtonDisabled : {}),
            }}
          >
            Previous
          </button>
          <span style={styles.paginationInfo}>
            Page {page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            style={{
              ...styles.paginationButton,
              ...(page === pagination.totalPages ? styles.paginationButtonDisabled : {}),
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1200px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  refreshButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  description: {
    fontSize: '0.95rem',
    color: '#6b7280',
    marginBottom: '2rem',
  },
  loading: {
    fontSize: '1rem',
    color: '#6b7280',
  },
  error: {
    padding: '1rem',
    backgroundColor: '#fee',
    color: '#c33',
    borderRadius: 'var(--radius-md)',
  },
  logList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#9ca3af',
  },
  logEntry: {
    display: 'flex',
    gap: '1.5rem',
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
  },
  actionIcon: {
    fontSize: '2rem',
    flexShrink: 0,
  },
  logContent: {
    flex: 1,
  },
  logHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #e5e7eb',
  },
  actionBadge: {
    display: 'inline-block',
    padding: '0.5rem 1rem',
    color: 'white',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9rem',
    fontWeight: '700',
  },
  logDate: {
    fontSize: '0.9rem',
    color: '#9ca3af',
  },
  logDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  logRow: {
    display: 'flex',
    gap: '0.75rem',
  },
  logLabel: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#6b7280',
    minWidth: '120px',
  },
  logValue: {
    fontSize: '0.9rem',
    color: '#1e293b',
    wordBreak: 'break-word',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '2rem',
  },
  paginationButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    color: '#374151',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  paginationInfo: {
    fontSize: '0.95rem',
    color: '#6b7280',
  },
};
